import { WindowRole } from 'features/timeSeries/components/windowsTool/windowRoleColors'
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'

export type WindowDrawRole = WindowRole | null

export interface DetectionEvalDigest {
  hits: number
  evaluable: number
  fpPer30d: number | null
}

/** Session-only Build UI state, split from the lab session so a toolbar
    toggle or shelf checkbox never re-renders every config consumer. */
interface BuildUiContextValue {
  windowDrawRole: WindowDrawRole
  setWindowDrawRole: Dispatch<SetStateAction<WindowDrawRole>>
  showSplitWindows: boolean
  setShowSplitWindows: Dispatch<SetStateAction<boolean>>
  showTargetBounds: boolean
  setShowTargetBounds: Dispatch<SetStateAction<boolean>>
  showOutOfBoundsFlags: boolean
  setShowOutOfBoundsFlags: Dispatch<SetStateAction<boolean>>
  showResidual: boolean
  setShowResidual: Dispatch<SetStateAction<boolean>>
  compareSelection: string[]
  setCompareSelection: Dispatch<SetStateAction<string[]>>
  evalSummaries: Record<string, DetectionEvalDigest>
  setEvalSummaries: Dispatch<
    SetStateAction<Record<string, DetectionEvalDigest>>
  >
}

const BuildUiContext = createContext<BuildUiContextValue | null>(null)

export const BuildUiProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [windowDrawRole, setWindowDrawRole] = useState<WindowDrawRole>(null)
  const [showSplitWindows, setShowSplitWindows] = useState(true)
  const [showTargetBounds, setShowTargetBounds] = useState(true)
  const [showOutOfBoundsFlags, setShowOutOfBoundsFlags] = useState(true)
  const [showResidual, setShowResidual] = useState(true)
  const [compareSelection, setCompareSelection] = useState<string[]>([])
  const [evalSummaries, setEvalSummaries] = useState<
    Record<string, DetectionEvalDigest>
  >({})

  const value = useMemo(
    () => ({
      windowDrawRole,
      setWindowDrawRole,
      showSplitWindows,
      setShowSplitWindows,
      showTargetBounds,
      setShowTargetBounds,
      showOutOfBoundsFlags,
      setShowOutOfBoundsFlags,
      showResidual,
      setShowResidual,
      compareSelection,
      setCompareSelection,
      evalSummaries,
      setEvalSummaries,
    }),
    [
      windowDrawRole,
      showSplitWindows,
      showTargetBounds,
      showOutOfBoundsFlags,
      showResidual,
      compareSelection,
      evalSummaries,
    ]
  )

  return (
    <BuildUiContext.Provider value={value}>{children}</BuildUiContext.Provider>
  )
}

export const useBuildUi = (): BuildUiContextValue => {
  const context = useContext(BuildUiContext)
  if (!context) {
    throw new Error('useBuildUi must be used within a BuildUiProvider')
  }
  return context
}

const NOOP = (): void => undefined

/** Null-safe view for chart surfaces rendered without the provider. */
export const useBuildUiView = (): Pick<
  BuildUiContextValue,
  | 'windowDrawRole'
  | 'setWindowDrawRole'
  | 'showSplitWindows'
  | 'showTargetBounds'
  | 'showOutOfBoundsFlags'
  | 'showResidual'
> => {
  const context = useContext(BuildUiContext)
  return {
    windowDrawRole: context?.windowDrawRole ?? null,
    setWindowDrawRole: context?.setWindowDrawRole ?? NOOP,
    showSplitWindows: context?.showSplitWindows ?? true,
    showTargetBounds: context?.showTargetBounds ?? true,
    showOutOfBoundsFlags: context?.showOutOfBoundsFlags ?? true,
    showResidual: context?.showResidual ?? true,
  }
}
